import React, { useRef, useState } from 'react'

import { cn } from './cn'

// DOM mirrors Semantic UI React's Checkbox (modules/Checkbox/Checkbox.js) and Radio
// (addons/Radio/Radio.js) for the props the app uses. The root is a div (or `as`), with an
// `input.hidden` and a `label` inside it. The class words come first, in SUIR's order
// (ui, checked, fitted, radio, toggle, checkbox), as marker classes. `ui-checkbox` is a
// helper class for the pseudo-elements and sibling states in globals.css.
//
// Event handling is SUIR's: a label click toggles through the input when the label has an
// `id` and through handleChange when it does not; a root or label click from the mouse
// toggles; a keyboard Space on the focused input toggles through the native click and change.
// Mouse down moves focus to the input and calls preventDefault. A radio that is checked does
// not change.
//
// The styles are copied from Semantic UI 2.4.2 checkbox.css. Geometry and the `input`/`label`
// children are Tailwind rules on the root with `&.ui`, as Button does. Pseudo-elements and the
// state rules keyed on the input's sibling are in globals.css (`ui-checkbox`).

export type CheckboxData = CheckboxProps & { checked: boolean }

export type CheckboxProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'color' | 'content' | 'onChange' | 'onClick' | 'onMouseDown' | 'onMouseUp'
> & {
  as?: React.ElementType
  checked?: boolean
  children?: React.ReactNode
  id?: string
  label?: React.ReactNode
  name?: string
  // SUIR calls onChange(event, { ...props, checked }) with the proposed state.
  onChange?: (e: React.SyntheticEvent<HTMLElement>, data: CheckboxData) => void
  onClick?: (e: React.MouseEvent<HTMLElement>, data: CheckboxData) => void
  onMouseDown?: (e: React.MouseEvent<HTMLElement>, data: CheckboxData) => void
  onMouseUp?: (e: React.MouseEvent<HTMLElement>, data: CheckboxData) => void
  // SUIR: a disabled or read-only Checkbox does not toggle, and its input is out of the tab order.
  disabled?: boolean
  readOnly?: boolean
  tabIndex?: number
  toggle?: boolean
  value?: string
}

export type RadioProps = Omit<CheckboxProps, 'toggle'>

// `.ui.checkbox`: the root box, with the input hidden under the label.
const CHECKBOX_BASE = [
  '[&.ui]:relative [&.ui]:inline-block [&.ui]:[backface-visibility:hidden] [&.ui]:outline-none',
  '[&.ui]:align-baseline [&.ui]:not-italic [&.ui]:[font-size:1rem] [&.ui]:leading-[17px]',
  '[&.ui]:min-h-[17px] [&.ui]:min-w-[17px]',
  // `.ui.checkbox input[type=checkbox|radio]`. The input stays focusable and is only
  // transparent; `!inline-block` keeps Tailwind's `hidden` utility (the SUIR marker class
  // `hidden` on this input) from setting display:none.
  '[&.ui_input]:cursor-pointer [&.ui_input]:absolute [&.ui_input]:top-0 [&.ui_input]:left-0',
  '[&.ui_input]:!opacity-0 [&.ui_input]:outline-none [&.ui_input]:z-[3] [&.ui_input]:w-[17px] [&.ui_input]:h-[17px]',
  '[&.ui_input]:!inline-block [&.ui_input.hidden]:z-[-1]',
  // `input.hidden + label` is the selectable label.
  '[&.ui_input.hidden+label]:cursor-pointer [&.ui_input.hidden+label]:select-none',
  // `.ui.checkbox .box, .ui.checkbox label`.
  '[&.ui_label]:cursor-auto [&.ui_label]:relative [&.ui_label]:block [&.ui_label]:pl-[1.85714em]',
  '[&.ui_label]:outline-none [&.ui_label]:[font-size:1em]',
].join(' ')

const FITTED = '[&.ui.fitted_label]:!pl-0'

const RADIO = '[&.ui.radio]:min-h-[15px] [&.ui.radio_label]:pl-[1.85714em]'

// `.ui.toggle.checkbox`: the track is a pseudo-element (globals.css), and the label has room
// for it. Its `min-height` beats the base by specificity.
const TOGGLE = [
  '[&.ui.toggle]:min-h-[1.5rem] [&.ui.toggle_input]:w-[3.5rem] [&.ui.toggle_input]:h-[1.5rem]',
  '[&.ui.toggle_label]:min-h-[1.5rem] [&.ui.toggle_label]:pl-[4.5rem] [&.ui.toggle_label]:pt-[0.15em]',
].join(' ')

function isNil(value: unknown): value is null | undefined {
  return value === null || value === undefined
}

// SUIR's htmlInputAttrs. With aria-*, these go to the inner input, not the root (SUIR's
// partitionHTMLProps in Checkbox.js). Form.Field's aria wiring and `disabled` depend on it.
const HTML_INPUT_ATTRS = new Set<string>([
  'selected',
  'defaultValue',
  'defaultChecked',
  'accept',
  'autoCapitalize',
  'autoComplete',
  'autoCorrect',
  'autoFocus',
  'checked',
  'disabled',
  'enterKeyHint',
  'form',
  'id',
  'inputMode',
  'lang',
  'list',
  'max',
  'maxLength',
  'min',
  'minLength',
  'multiple',
  'name',
  'pattern',
  'placeholder',
  'readOnly',
  'required',
  'step',
  'title',
  'value',
])
// `type` is left out: the input sets it after `id`, as SUIR's attribute order does, and an
// undefined `type` spread first would move it to the front.

const CheckboxRoot = function CheckboxBase({
  as,
  checked: checkedProp,
  disabled,
  children: _children,
  className,
  id,
  label,
  name,
  onChange,
  onClick,
  onMouseDown,
  onMouseUp,
  radio,
  readOnly,
  tabIndex,
  toggle,
  value,
  ...rest
}: CheckboxProps & { radio?: boolean }) {
  const [innerChecked, setInnerChecked] = useState(false)
  // Controlled when `checked` is passed, as SUIR's auto-controlled state is.
  const checked = checkedProp !== undefined ? checkedProp : innerChecked

  const inputRef = useRef<HTMLInputElement>(null)
  const labelRef = useRef<HTMLLabelElement>(null)
  // Set by mouse up and cleared by the click that follows it.
  const isClickFromMouse = useRef(false)

  const Tag: React.ElementType = as ?? 'div'
  const type = radio ? 'radio' : 'checkbox'
  const hasId = !isNil(id)
  const canToggle = !disabled && !readOnly && !(radio && checked)

  // The data SUIR passes to every handler: all props, with the proposed checked state.
  const data = (nextChecked: boolean): CheckboxData =>
    ({
      ...rest,
      as,
      checked: nextChecked,
      className,
      id,
      label,
      name,
      disabled,
      radio,
      readOnly,
      tabIndex,
      toggle,
      value,
    }) as CheckboxData

  const handleChange = (e: React.SyntheticEvent<HTMLElement>) => {
    if (!canToggle) return
    onChange?.(e, data(!checked))
    if (checkedProp === undefined) setInnerChecked(!checked)
  }

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as Node
    const isInputClick = inputRef.current?.contains(target) ?? false
    const isLabelClick = labelRef.current?.contains(target) ?? false
    const isRootClick = !isLabelClick && !isInputClick
    // A label with an id forwards its click to the input, which is clicked again.
    const isLabelClickAndForwardedToInput = isLabelClick && hasId

    if (!isLabelClickAndForwardedToInput) {
      onClick?.(e, data(!checked))
    }

    if (isClickFromMouse.current) {
      isClickFromMouse.current = false

      if (isLabelClick && !hasId) handleChange(e)
      if (isRootClick) handleChange(e)
      if (isLabelClick && hasId) {
        // Stops the label's click from firing a second time through the input (SUIR #3433).
        e.stopPropagation()
      }
    }
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    onMouseDown?.(e, data(!!checked))
    if (!e.defaultPrevented) inputRef.current?.focus()
    // Keeps the element focused, as SUIR does.
    e.preventDefault()
  }

  const handleMouseUp = (e: React.MouseEvent<HTMLElement>) => {
    isClickFromMouse.current = true
    onMouseUp?.(e, data(!!checked))
  }

  const markers = [
    'ui',
    checked && 'checked',
    disabled && 'disabled',
    isNil(label) && 'fitted',
    radio && 'radio',
    readOnly && 'read-only',
    toggle && 'toggle',
    'checkbox',
    'ui-checkbox',
  ]
    .filter(Boolean)
    .join(' ')

  const htmlInputProps: Record<string, unknown> = {}
  const rootProps: Record<string, unknown> = {}
  for (const [prop, value] of Object.entries(rest)) {
    if (HTML_INPUT_ATTRS.has(prop) || /^aria-/.test(prop)) {
      htmlInputProps[prop] = value
    } else {
      rootProps[prop] = value
    }
  }

  return (
    <Tag
      {...rootProps}
      className={`${markers} ${cn(
        CHECKBOX_BASE,
        isNil(label) && FITTED,
        radio && RADIO,
        toggle && TOGGLE,
        className,
      )}`}
      onChange={handleChange}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <input
        {...htmlInputProps}
        checked={checked}
        disabled={disabled}
        className="hidden"
        id={id}
        name={name}
        readOnly
        ref={inputRef}
        tabIndex={tabIndex ?? (disabled ? -1 : 0)}
        type={type}
        value={value}
      />
      <label htmlFor={id} ref={labelRef}>
        {label}
      </label>
    </Tag>
  )
}

// SUIR's Checkbox sets `type` from its props, so the input type is `checkbox` unless this is
// a Radio.
export function Checkbox(props: CheckboxProps) {
  return <CheckboxRoot {...props} />
}

export function Radio(props: RadioProps) {
  return <CheckboxRoot {...props} radio />
}
